import React, { useState, useContext, useEffect } from "react";
import { STATUS_MESSAGE_COLORS, BORDERS } from "../../../config";

import { CoreContext } from "../../../context/global/CoreContext";
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

import { clockOutUser } from "../../../services/api/timekeeping";

import {
  getCurrentTimeInWindow,
  addHoursTo24TimeString,
} from "../../../utils/datetime";

// Import icons
import { Clock } from "../../../components/icons/svg/outline/index";
import { Button } from "../../../components/ui";
import { ModalWrapper } from "../../../components/ui";

/**
 * ClockOut component allows users to record their clock-out time.
 *
 * The component provides a time input defaulted to either the current time or 15:00 (3:00 PM),
 * whichever is later. On successful clock-out, it updates the shift data in the TimekeepingContext
 * and displays a success message.
 *
 * @component
 * @param {Object} props
 * @param {string} props.date - The date for which the clock-out is being recorded (YYYY-MM-DD format)
 *
 * @example
 * <ClockOut date="2023-06-15" />
 *
 * @returns {JSX.Element} The ClockOut component
 */
const ClockOut = ({ date, startVisible, setDayComplete }) => {
  const { user } = useContext(CoreContext);
  const {
    showClockOutContent,
    setShowClockOutContent,
    fetchWeeklyShifts,
    dailyShiftData,
    // Get message state from context
    message,
    setMessageDebounced,
  } = useContext(TimekeepingContext); // State management
  const currentShift = dailyShiftData?.find(
    (shift) => shift.stage === "Clocked In"
  );
  const [finishTime, setFinishTime] = useState("15:00"); // Default time out set to 15:00
  const [showBreaksModal, setShowBreaksModal] = useState(false); // Modal visibility
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Show clock out content if startVisible is true
  useEffect(() => {
    if (startVisible) {
      setShowClockOutContent(true);
    }
  }, [startVisible, setShowClockOutContent]);

  // Set default time to clock in time + 8 or current time if within -2 hour window
  useEffect(() => {
    if (currentShift && currentShift.startTime) {
      setFinishTime(
        getCurrentTimeInWindow(
          addHoursTo24TimeString(currentShift.startTime, 6),
          addHoursTo24TimeString(currentShift.startTime, 8),
          addHoursTo24TimeString(currentShift.startTime, 8)
        )
      );
    }
  }, [currentShift]);

  const handleClockOut = async () => {
    // Get the clock out shift data, there should only ever be one clocked in shift per day

    if (!finishTime) {
      setMessageDebounced({ type: "error", text: "Please enter a time" });
      return;
    }

    if (finishTime < currentShift?.startTime) {
      setMessageDebounced({
        type: "error",
        text: "Clock out time cannot be before clock in time",
      });
      return;
    }

    // Show the breaks modal instead of immediately clocking out
    setShowBreaksModal(true);
  };

  const handleBreaksConfirmation = async (breaksCompleted) => {
    setShowBreaksModal(false);

    try {
      setIsLoading(true);
      const result = await clockOutUser({
        date,
        finishTime,
        userId: user?.id,
        breaksCompleted,
      });

      if (result.success) {
        setMessageDebounced({ type: "success", text: result.message });
        setIsAnimating(true); // Hide clock out box after successful clock out
        setTimeout(() => {
          fetchWeeklyShifts(); // Fetch updated shift data
        }, 800); // Delay to allow animations to complete
        setDayComplete(true); // Set state to not allow clocking in again
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || "Clock out failed",
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error clocking out:", error);
      setMessageDebounced({
        type: "error",
        text: "An error occurred while clocking out",
      });
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Clock Out Form */}
      <div
        className={`
        opacity-0 mt-2
        ${showClockOutContent ? "animate-expand-down" : ""} 
        bg-gray-100 ${isAnimating ? "py-0 px-6" : "p-2"} rounded-md 
        transition-all duration-1000 ease-in-out`}
      >
        <div
          className={`CLOCK-OUT w-full
          ${isAnimating ? "animate-shrink-hide" : showClockOutContent ? "opacity-100" : "opacity-0"}
          transition-all duration-1000 delay-500 origin-top`}
        >
          <h2 className="flex items-center text-lg text-black font-medium mb-4">
            <Clock className="text-gray-700 mr-2" />
            Clock Out
          </h2>{" "}
          {message && !message.text.includes("schedule") && (
            <div
              className={`mb-4 p-3 ${BORDERS.radius.sm} ${STATUS_MESSAGE_COLORS[message.type] || STATUS_MESSAGE_COLORS.success}`}
            >
              {message.text}
            </div>
          )}{" "}
          {/* Time Out */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Out
            </label>
            <input
              type="time"
              value={getCurrentTimeInWindow(
                finishTime,
                addHoursTo24TimeString(finishTime, -2),
                finishTime
              )}
              onChange={(e) => setFinishTime(e.target.value)}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />{" "}
          </div>{" "}
          {/* Clock Out Button */}
          <Button
            onClick={() => {
              handleClockOut();
            }}
            variant={isLoading ? "disabled" : "secondary"}
            text={isLoading ? "" : "Clock Out"}
            icon={isLoading ? "spinner" : null}
            className="py-3 px-6 mt-8 sm:mt-0 w-full"
          />
        </div>
      </div>

      {/* Breaks Confirmation Modal */}
      <ModalWrapper
        isOpen={showBreaksModal}
        onClose={() => setShowBreaksModal(false)}
        closeButton={false}
      >
        <div className="text-center p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Break Confirmation
          </h2>
          <p className="text-gray-600 mb-8">
            Did you take all legally required breaks during this shift?
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => handleBreaksConfirmation(true)}
              variant="primary"
              text="Yes, I did"
              className="px-6 py-2"
            />
            <Button
              onClick={() => handleBreaksConfirmation(false)}
              variant="secondary"
              text="No, I didn't"
              className="px-6 py-2"
            />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default ClockOut;
